'use client';

import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { useIntl } from 'react-intl';

import { sourceGroundedMessages } from './sourceGrounded.messages';

import { useArtifactPanelNavigation } from '../../../data/projects/artifacts/useArtifactPanelNavigation';
import {
  scrollToSourceCitation,
  type SourceGroundedReference,
} from '../../../data/projects/source-grounded-citations';
import {
  kindDisplayName,
  parseSourceGroundedLabel,
} from '../../../data/projects/source-grounded-label';
import {
  isSourceGroundedReferenceNavigable,
  resolveSourceGroundedArtifact,
  type SourceGroundedSessionTabs,
} from '../../../data/projects/source-grounded-navigation';
import { iconForSourceLabel } from './source-grounded-icons';

export const SourceGroundedReferencesFooter = ({
  references,
  className,
  compactCitationsEnabled,
  sessionTabs,
}: {
  references: SourceGroundedReference[];
  className?: string;
  /**
   * `release-scribe-work-source-citation-chips`, resolved by the composition
   * point — a flag is app-resolved by rule and never read from inside a package.
   */
  compactCitationsEnabled: boolean;
  sessionTabs: SourceGroundedSessionTabs;
}) => {
  const intl = useIntl();
  const { openArtifact } = useArtifactPanelNavigation();

  if (references.length === 0) return null;

  const handleReferenceClick = (ref: SourceGroundedReference) => {
    const artifact = resolveSourceGroundedArtifact(ref, sessionTabs);
    if (artifact) {
      openArtifact(artifact);
      return;
    }
    scrollToSourceCitation(ref.id);
  };

  return (
    <div
      className={cn('mt-3 flex flex-col gap-2 border-t border-border pt-3', className)}
      data-testid="source-grounded-references-footer"
    >
      <TypographyCaptionBold className="text-text-secondary">
        {intl.formatMessage(sourceGroundedMessages.sourcesHeading, {
          count: references.length,
        })}
      </TypographyCaptionBold>
      <div className="flex flex-wrap gap-1.5">
        {references.map((ref) => {
          const Icon = iconForSourceLabel(ref.title ?? ref.label);
          const parsed = parseSourceGroundedLabel(ref.label);
          const title = ref.title ?? parsed.title;
          const subtitle = ref.subtitle ?? parsed.subtitle;
          const kind = ref.kind ?? parsed.kind;
          const displayTitle =
            compactCitationsEnabled &&
            ref.provenance === 'render_sources' &&
            kind === 'consult_note'
              ? intl.formatMessage(sourceGroundedMessages.noteTitle)
              : title;
          const navigable = isSourceGroundedReferenceNavigable(ref);
          const referenceTitle = ref.detail
            ? intl.formatMessage(sourceGroundedMessages.titleDetailTitle, {
                label: ref.label,
                detail: ref.detail,
              })
            : subtitle
              ? intl.formatMessage(sourceGroundedMessages.titleSubtitleTitle, {
                  title,
                  subtitle,
                })
              : title;
          return (
            <button
              key={ref.id}
              type="button"
              data-testid={`source-grounded-ref-${ref.id}`}
              className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-full border border-border',
                'bg-surface py-1 pe-2.5 ps-1 text-left text-text-secondary',
                'transition-colors hover:border-border-hover hover:bg-background-secondary'
              )}
              title={referenceTitle}
              aria-label={
                navigable
                  ? intl.formatMessage(sourceGroundedMessages.openSourceAria, {
                      id: ref.id,
                      title: displayTitle,
                    })
                  : intl.formatMessage(sourceGroundedMessages.jumpToSourceAria, {
                      id: ref.id,
                      title: displayTitle,
                    })
              }
              onClick={() => handleReferenceClick(ref)}
            >
              <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-validation-info-muted text-[10px] font-semibold tabular-nums text-validation-info">
                {ref.id}
              </span>
              <Icon className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-xs font-medium text-text-primary">
                  {displayTitle}
                </span>
                <span className="truncate text-[10px] text-text-tertiary">
                  {subtitle ?? kindDisplayName(intl, kind)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
